    /// <reference path="../../typings/browser.d.ts" />

namespace Application.Models {
    'use strict';

    export interface IHalElement<T> {
        _links: IHalLinks;
    }

    export interface IHalList<T> extends IHalElement<T> {
        _embedded: IHalEmbedded<T>;
        page: IPage;
    }

    export interface IHalEmbedded<T> {
    }

    export interface IHalLinks {
        self: IHalLink;
        pipes?: IHalLink;
    }

    export interface IHalLink {
        href: string;
    }

    export interface IHalRelLink {
        href: string;
        rel: string;
    }
    
    export interface IdList {
        ids: number[];
    }

    /**
     * Utility class for automatic conversion of Hal elements into local model objects.
     * The conversion is done via a factory method that the user must provide. Then it copies
     * all elements from the Hal element into the model object except for "_links".
     * Then it extract the self href element and inject it into "href" property of the object.
     */
    export class HalConverter {
        private static HREF_PROPERTY: string = 'href';
        private static LINKS_PROPERTY: string = '_links';
        private static LINK_ARRAY_PROPERTY: string = 'links';
        public static convertPaginatedList<T>(halList: IHalList<T>, constructor: () => T): Models.IPaginatedList<T> {
            let embedded: IHalEmbedded<T> = halList._embedded;
            let convertedList: Array<T> = [];
            if (embedded) {
                let list: Array<IHalElement<T>> = embedded[Object.keys(embedded)[0]];
                convertedList = list.map((element: IHalElement<T>) => {
                        return HalConverter.convertElement(element, constructor);
                    }
                );
            }
            return {
                elements: convertedList,
                page: halList.page,
            };
        }

        public static convertList<T, S extends T>(halList: IHalList<T>, constructor: () => S): Array<S> {
            let embedded: IHalEmbedded<T> = halList._embedded;
            if (embedded !== undefined) {
                let list: Array<IHalElement<T>> = [];
                for (let x: number = 0; x < Object.keys(embedded).length; x++) {
                    list = list.concat(embedded[Object.keys(embedded)[x]]);
                }
                
                return list.map((element: IHalElement<T>) => {
                    return HalConverter.convertElement(element, constructor);
                });
            }
            return []; 
        }

        public static convertElement<T, S extends T>(element: IHalElement<T>, constructor: () => S): S {
            let newElement: S = constructor();
            Object.keys(element).forEach((property: string) => {
                if (property !== this.LINKS_PROPERTY) {
                    // TODO: Convert NULLs to undefined to avoid double checks through the code and check that everyhing works
                    // newElement[property] = element[property] === null ? undefined : element[property];
                    newElement[property] = element[property];
                } 
            });

            if (element._links !== undefined) {
                newElement[this.HREF_PROPERTY] = element._links.self.href;
                // the rest of the links are completely useless. Until we find a better way
                // to manage them we will ignore them. 
                Object.keys(element._links).forEach((property: string) => {
                    let idPos: number = property.search("_id");
                    if (idPos !== -1) {
                        let entityName: string = property.substring(0, idPos);
                        newElement[entityName] = element._links[property];
                        let templatePos: number = newElement[entityName].href.search("{");
                        if (templatePos > 0) {
                            // we must remove the template part
                            let href: string = newElement[entityName].href;
                            href = href.substring(0, templatePos);
                            newElement[entityName].href = href;
                        }
                    }
                });
            }

            if (element[this.LINK_ARRAY_PROPERTY] !== undefined) {
                this.convertLinkArray(newElement, element[this.LINK_ARRAY_PROPERTY]);
            }

            return newElement;
        }

        public static selfConvertLinks<T>(element: T): void {
            let links: IHalLinks = element[this.LINKS_PROPERTY];
            if (!links) {
                let linkArray: IHalRelLink[] = element[this.LINK_ARRAY_PROPERTY];
                this.convertLinkArray(element, linkArray);
            } else if (links)
                this.convertLinks(element, links);
        }

        public static convertLinks<T>(newElement: T, links: IHalLinks): void {
            newElement[this.HREF_PROPERTY] = links.self.href;
            // the rest of the links are completely useless. Until we find a better way
            // to manage them we will ignore them. 
            Object.keys(links).forEach((property: string) => {                
                let idPos: number = property.search("_id");
                if (idPos !== -1) {
                    let entityName: string = property.substring(0, idPos);
                    if (!newElement[entityName])
                         newElement[entityName] = {};
                    let targetProperty: Object = newElement[entityName];
                    targetProperty[this.HREF_PROPERTY] = links[property].href;
                    let templatePos: number = targetProperty[this.HREF_PROPERTY].search("{");
                    if (templatePos > 0) {
                        // we must remove the template part
                        let href: string = targetProperty[this.HREF_PROPERTY];
                        href = href.substring(0, templatePos);
                        targetProperty[this.HREF_PROPERTY] = href;
                    }
                }
            });
        }

        public static convertLinkArray<T>(newElement: T, links: IHalRelLink[]): void {
            links.forEach((link: IHalRelLink) => {
                if (link.rel === "self") {
                    newElement[this.HREF_PROPERTY] = link.href;
                } else {
                    let idPos: number = link.rel.search("_id");
                    if (idPos !== -1) {
                        let entityName: string = link.rel.substring(0, idPos);
                        if (!newElement[entityName])
                            newElement[entityName] = {};
                        let targetProperty: Object = newElement[entityName];
                        targetProperty[this.HREF_PROPERTY] = link.href;
                        let templatePos: number = targetProperty[this.HREF_PROPERTY].search("{");
                        if (templatePos > 0) {
                            // we must remove the template part
                            let href: string = targetProperty[this.HREF_PROPERTY];
                            href = href.substring(0, templatePos);
                            targetProperty[this.HREF_PROPERTY] = href;
                        }
                    }
                }

            });
        }
    }    
}
